/* The pickers a piece of work needs: people for a step, and the pack's own
 * fields.
 *
 * A pack's fields are drawn by the engine's own controls (the same Select,
 * Date, Link and number inputs every ERPNext form uses), so a pack never
 * brings a control of its own. People are picked from those who hold the role
 * the step needs: offering somebody who cannot take the step only produces a
 * refusal later.
 */
import { useEffect, useMemo, useState } from 'react'
import { buildRegistry, renderWith } from '../controls'
import type { ErpError } from '../api'
import { useErp } from '../context'
import type { DocField, FieldType } from '../types'
import type { DepartmentTree, PackField, WorkPerson } from './types'
import { rolesOf } from './words'

const registry = buildRegistry(() => [])

/** A pack field, as the engine would draw it on any form. */
export function PackFieldInput({ field, value, onChange, error, required }: {
  field: PackField; value: unknown; onChange: (v: unknown) => void
  error?: string; required?: boolean
}) {
  const doc: DocField = useMemo(() => ({
    fieldname: field.fieldname,
    fieldtype: field.fieldtype as FieldType,
    label: field.label,
    options: Array.isArray(field.options) ? field.options.join('\n') : field.options,
    ...(required ? { reqd: 1 as const } : {}),
  }), [field, required])
  return renderWith(registry, {
    field: doc, value, doc: {}, readOnly: false, required: Boolean(required), error, onChange,
  })
}

/** Any engine control by hand: a Link to Project, a Date for the deadline. */
export function PlainField({ fieldname, label, fieldtype, options, value, onChange, error, required }: {
  fieldname: string; label: string; fieldtype: FieldType; options?: string
  value: unknown; onChange: (v: unknown) => void; error?: string; required?: boolean
}) {
  const field: DocField = useMemo(() => ({
    fieldname, label, fieldtype, options, ...(required ? { reqd: 1 as const } : {}),
  }), [fieldname, label, fieldtype, options, required])
  return renderWith(registry, {
    field, value, doc: {}, readOnly: false, required: Boolean(required), error, onChange,
  })
}

const people = new Map<string, Promise<WorkPerson[]>>()

/** People who hold one of `role`, or a department's people when `department` is given. */
function usePeople(role: string | string[] | undefined, department?: string) {
  const erp = useErp()
  const roles = rolesOf(role)
  const key = department ? `dept:${department}` : roles.length ? roles.join(',') : 'everyone'
  const [list, setList] = useState<WorkPerson[] | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    setList(null); setFailed(null)
    let pending = people.get(key)
    if (!pending) {
      pending = (department ? erp.departmentPeople(department) : erp.workPeople(roles))
        .then((r) => r.people)
      people.set(key, pending)
      pending.catch(() => people.delete(key))
    }
    pending.then((l) => { if (live) setList(l) })
           .catch((e) => { if (live) setFailed((e as ErpError).message) })
    return () => { live = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erp, key])
  return { list, failed }
}

/** How a person reads in a list: their name, and what the org chart says of them. */
export function personLine(p: WorkPerson): string {
  const about = [p.designation, p.department].filter(Boolean).join(', ')
  return about ? `${p.name} — ${about}` : p.name !== p.email ? `${p.name} — ${p.email}` : p.name
}

/** One person or several, from those who hold `role` (one or several), or from a department. */
export function PeoplePicker({ id, label, role, department, value, onChange, multiple, error, required, hint }: {
  id: string; label: string; role?: string | string[]; department?: string; value: string[]
  onChange: (v: string[]) => void; multiple?: boolean; error?: string; required?: boolean
  hint?: string
}) {
  const { list, failed } = usePeople(role, department)
  const byEmail = useMemo(() => new Map((list ?? []).map((p) => [p.email, p])), [list])
  const nameOf = (email: string) => byEmail.get(email)?.name ?? email
  const left = (list ?? []).filter((p) => !value.includes(p.email))
  const nobody = list !== null && list.length === 0

  return (
    <div className="field">
      <label htmlFor={id}>{label}{required ? <span aria-hidden="true"> *</span> : null}</label>
      {multiple && value.length ? (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
          {value.map((email) => (
            <span key={email} className="chip chip-info" style={{ gap: 4 }}>
              {nameOf(email)}
              <button type="button" className="btn btn-quiet btn-sm" style={{ minHeight: 20, padding: '0 4px' }}
                      aria-label={`Remove ${nameOf(email)}`}
                      onClick={() => onChange(value.filter((v) => v !== email))}>×</button>
            </span>
          ))}
        </div>
      ) : null}
      <select
        id={id} className="select" aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}_err` : undefined}
        value={multiple ? '' : value[0] ?? ''}
        disabled={list === null || nobody}
        onChange={(e) => {
          const picked = e.target.value
          if (!picked) { if (!multiple) onChange([]); return }
          onChange(multiple ? [...value, picked] : [picked])
        }}
      >
        <option value="">
          {list === null ? 'Loading people…'
            : nobody ? 'Nobody holds this role yet'
            : multiple ? (value.length ? 'Add another…' : 'Choose…') : 'Choose…'}
        </option>
        {(multiple ? left : list ?? []).map((p) => (
          <option key={p.email} value={p.email}>{personLine(p)}</option>
        ))}
      </select>
      {multiple && department && left.length > 1 ? (
        <div>
          <button type="button" className="btn btn-quiet btn-sm"
                  onClick={() => onChange([...value, ...left.map((p) => p.email)])}>
            Everyone in this department ({left.length})
          </button>
        </div>
      ) : null}
      {nobody ? (
        // Never an empty dropdown without the way to fill it.
        <div className="small muted">
          {department
            ? 'Nobody is in this department yet. Put people in it in the Store (People), or place a department under it in Set up → Industry packs.'
            : <>Give someone the <b>{rolesOf(role).join(' or ')}</b> role first, in Set up → Industry packs → Who does what.</>}
        </div>
      ) : null}
      {failed ? <div className="small" style={{ color: 'var(--danger)' }}>{failed}</div> : null}
      {hint && !error ? <div className="small muted">{hint}</div> : null}
      {error ? <div id={`${id}_err`} className="small" style={{ color: 'var(--danger)' }}>{error}</div> : null}
    </div>
  )
}

let departments: Promise<DepartmentTree> | null = null

/** A department: the ERP's, with the organisation's own placed under each, so a person picks
 *  "Billing" and the work is filed under Finance & Accounts as well. `only` keeps the ERP
 *  departments a kind of work names (by label). */
export function DepartmentPicker({ id, value, onChange, only, error, required }: {
  id: string; value: string; onChange: (v: string) => void; only?: string[]; error?: string
  required?: boolean
}) {
  const erp = useErp()
  const [tree, setTree] = useState<DepartmentTree | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    if (!departments) {
      departments = erp.workDepartments()
      departments.catch(() => { departments = null })
    }
    departments.then((t) => { if (live) setTree(t) })
               .catch((e) => { if (live) setFailed((e as ErpError).message) })
    return () => { live = false }
  }, [erp])
  const shown = (tree?.departments ?? []).filter((d) => !only?.length || only.includes(d.label))
  return (
    <div className="field">
      <label htmlFor={id}>Department{required ? <span aria-hidden="true"> *</span> : null}</label>
      <select id={id} className="select" value={value} disabled={!tree}
              aria-invalid={error ? true : undefined} onChange={(e) => onChange(e.target.value)}>
        <option value="">{tree ? 'Choose…' : 'Loading departments…'}</option>
        {shown.map((d) => (
          <optgroup key={d.name} label={d.label}>
            <option value={d.name}>{d.label}{d.people ? ` (${d.people})` : ''}</option>
            {d.children.map((c) => (
              <option key={c.name} value={c.name}>{'\u00a0\u00a0'}{c.label}{c.people ? ` (${c.people})` : ''}</option>
            ))}
          </optgroup>
        ))}
      </select>
      {tree && tree.unplaced ? (
        <div className="small muted">
          {tree.unplaced === 1 ? 'One of your departments is' : `${tree.unplaced} of your departments are`} not
          placed under these yet: the account owner places them in Set up → Industry packs → Departments.
        </div>
      ) : null}
      {failed ? <div className="small" style={{ color: 'var(--danger)' }}>{failed}</div> : null}
      {error ? <div className="small" style={{ color: 'var(--danger)' }}>{error}</div> : null}
    </div>
  )
}

/** Forget what pickers read, after the departments or roles change. */
export function forgetPickers() { people.clear(); departments = null }

/** People by name rather than address, wherever a screen lists them. */
export function usePersonName(): (email: string | null | undefined) => string {
  const { list } = usePeople('everyone')
  const byEmail = useMemo(() => new Map((list ?? []).map((p) => [p.email, p.name])), [list])
  return (email) => (email ? byEmail.get(email) ?? email : '')
}
